import Organisation from "callable-data-objects/dist/models/organisation/Organisation";
import useCheckRole from "Hooks/APICheckRole";
import { useOrganisation, useOrgMetrics } from "Hooks/Endpoints";
import { isEmpty } from "lodash";
import APIAccountProvider from "Providers/APIAccountProvider";
import APIOrgChargeProvider from "Providers/APIOrgChargeProvider";
import APIOrgMetricsProvider from "Providers/APIOrgMetricsProvider";
import APIRoleProvider from "Providers/APIRoleProvider";
import APIUserProvider from "Providers/APIUserProvider";
import React, { Fragment, useEffect, useState } from "react";
import {
  Breadcrumb,
  Button,
  Dimmer,
  Grid,
  Header,
  Icon,
  Loader,
  Menu,
  Segment,
} from "semantic-ui-react";
import OrganisationCustomers from "./OrganisationCustomers";
import OrganisationDeleteForm from "./OrganisationDeleteForm";
import OrganisationGeneralForm from "./OrganisationGeneralForm";
import OrganisationRoles from "./OrganisationRoles";
import OrganisationServiceCharges from "./OrganisationServiceCharges";
import OrganisationUsers from "./OrganisationUsers";

const OrganisationDetails = () => {
  const { hasRole, roles } = useCheckRole();
  const {
    organisation,
    unselect,
    submitting,
    xeroURL,
    authorise,
    xeroAuthorised,
  } = useOrganisation();
  const [activeMenu, setActiveMenu] = useState<string>("customers");

  useEffect(() => {
    if (xeroURL) {
      console.log(xeroURL);
      window.open(xeroURL, "_blank");
    }
  }, [xeroURL]);

  return (
    <Fragment>
      <Dimmer.Dimmable dimmed={submitting}>
        {menu(organisation, unselect)}
        {renderView(
          activeMenu,
          organisation,
          setActiveMenu,
          hasRole,
          roles,
          xeroURL,
          authorise,
          xeroAuthorised
        )}
        <Dimmer inverted active={submitting}>
          <Loader />
        </Dimmer>
      </Dimmer.Dimmable>
    </Fragment>
  );
};

const renderView = (
  activeMenu: string,
  organisation: Organisation,
  setActiveMenu: React.Dispatch<React.SetStateAction<string>>,
  hasRole: Function,
  roles: any,
  xeroURL: String,
  authorise: Function,
  xeroAuthorised: boolean
) => {
  return (
    <Grid padded stackable>
      <Grid.Column width={2}>
        <Menu pointing vertical stackable fluid>
          <Menu.Item
            name='accounts'
            active={activeMenu === "customers"}
            onClick={() => setActiveMenu("customers")}
          />

          {hasRole(roles.organisation.user.roles.view) && (
            <Menu.Item
              name='users'
              onClick={() => setActiveMenu("users")}
              active={activeMenu === "users"}
            >
              Users
            </Menu.Item>
          )}

          {hasRole(roles.organisation.role.roles.view) && (
            <Menu.Item
              name='roles'
              onClick={() => setActiveMenu("roles")}
              active={activeMenu === "roles"}
            >
              Roles
            </Menu.Item>
          )}

          {hasRole(roles.organisation.charge.roles.view) && (
            <Menu.Item
              name='charges'
              active={activeMenu === "charges"}
              onClick={() => setActiveMenu("charges")}
            />
          )}

          {hasRole(roles.organisation.roles.edit) && (
            <Menu.Item
              name='edit'
              active={activeMenu === "general"}
              onClick={() => setActiveMenu("general")}
            />
          )}

          {hasRole(roles.organisation.roles.edit) &&
            organisation?.cdrSettings?.type === "xero" &&
            !isEmpty(organisation?.cdrSettings?.username) &&
            !isEmpty(organisation?.cdrSettings?.password) && (
              <Menu.Item
                name='xero'
                active={activeMenu === "xero"}
                onClick={() => setActiveMenu("xero")}
              />
            )}
          {hasRole(roles.organisation.roles.delete) && (
            <Menu.Item
              name='delete'
              onClick={() => setActiveMenu("delete")}
              active={activeMenu === "delete"}
            >
              Delete
            </Menu.Item>
          )}
        </Menu>
      </Grid.Column>
      <Grid.Column width={14}>
        {activeMenu === "general" && <OrganisationGeneralForm />}

        {activeMenu === "customers" && (
          <APIAccountProvider>
            <APIOrgMetricsProvider>
              <OrganisationCustomers />
            </APIOrgMetricsProvider>
          </APIAccountProvider>
        )}

        {activeMenu === "charges" && (
          <APIOrgChargeProvider>
            <APIAccountProvider>
              <OrganisationServiceCharges />
            </APIAccountProvider>
          </APIOrgChargeProvider>
        )}

        {activeMenu === "users" && (
          <APIUserProvider organisation={organisation}>
            <OrganisationUsers />
          </APIUserProvider>
        )}

        {activeMenu === "roles" && (
          <APIRoleProvider>
            <OrganisationRoles organisation={organisation} />
          </APIRoleProvider>
        )}

        {activeMenu === "xero" && (
          <div>
            {!xeroAuthorised && (
              <Segment placeholder>
                <Header icon>Authorise Xero</Header>
                <Button primary onClick={() => authorise()}>
                  Authorise
                </Button>
              </Segment>
            )}
            {xeroAuthorised && (
              <Segment placeholder>
                <Header icon>Xero is Authorised</Header>
              </Segment>
            )}
          </div>
        )}

        {activeMenu === "delete" && <OrganisationDeleteForm />}
      </Grid.Column>
    </Grid>
  );
};

const menu = (organisation: Organisation, unselect: Function) => {
  return (
    <Menu stackable pointing>
      <Menu.Item header>{breadcrumb(organisation, unselect)}</Menu.Item>
    </Menu>
  );
};

const breadcrumb = (organisation: Organisation, unselect: Function) => {
  return (
    <Breadcrumb>
      <Breadcrumb.Section link onClick={() => unselect()}>
        Organisations
      </Breadcrumb.Section>
      <Breadcrumb.Divider />
      <Breadcrumb.Section>{organisation.name}</Breadcrumb.Section>
    </Breadcrumb>
  );
};

export default OrganisationDetails;
